import { constructAccessToken, getBaseApiURL, resolveNextApiUrl } from '@/app/utils/apiCommonUtils';
import { LANGUAGE_COOKIE_KEY, STORED_COOKIE_REQUEST_KEY } from '@/app/constants';
import * as Sentry from '@sentry/nextjs';
import { getCountryConfig } from '@/app/utils/countryConfig';

import {
  ProjectsApi,
  DonationsApi,
  MeApi,
  ProjectFaqsApi,
  ProjectNewsApi,
  Configuration,
  SearchApi,
  TaxonomyApi,
  SubprojectsApi,
  ProjectRewardsApi,
  RequestContext,
  HTTPQuery,
} from '@/app/api';

const countryConfig = getCountryConfig();

const getLanguageCookieSilently = async () => {
  try {
    const params = new URLSearchParams();
    params.append(STORED_COOKIE_REQUEST_KEY, LANGUAGE_COOKIE_KEY);

    const languageCookieResponse = await fetch(`${resolveNextApiUrl()}/next-api/cookie?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'include',
    });

    return (await languageCookieResponse?.json?.())?.cookie;
  } catch (error) {
    Sentry.captureException(error);
  }
};

const acceptLanguageMiddleware = {
  pre: async (context: RequestContext) => {
    const languageCookie = await getLanguageCookieSilently();
    const language = languageCookie?.value ? languageCookie.value : countryConfig.fallbackLanguage;

    return {
      ...context,
      init: {
        ...context.init,
        headers: {
          ...context.init.headers,
          'Accept-Language': language,
        },
      },
    };
  },
};

// custom
export function querystring(params: HTTPQuery, prefix: string = ''): string {
  return Object.keys(params)
    .map((key) => querystringSingleKey(key, params[key], prefix))
    .filter((part) => part.length > 0)
    .join('&');
}

/**
 * Custom function to serialize query parameters properly.
 *
 * Why is this necessary?
 * - Default serialization (like `JSON.stringify`) does not format arrays correctly for query parameters.
 * - OpenAPI and many APIs expect array parameters to be formatted as `statuses[]=active&statuses[]=finished`.
 *
 * This function ensures:
 * - Arrays are serialized as `key[]=value1&key[]=value2`.
 * - Nested objects are properly serialized using recursion.
 * - Standard primitive values are encoded correctly.
 */
function querystringSingleKey(key: string, value: any, prefix: string = ''): string {
  const fullKey = prefix ? `${prefix}[${key}]` : key;

  if (Array.isArray(value)) {
    // Convert array elements into query params with "[]" syntax
    return value.map((val) => `${encodeURIComponent(fullKey)}[]=${encodeURIComponent(val)}`).join('&');
  } else if (typeof value === 'object' && value !== null) {
    return querystring(value, fullKey);
  } else {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value)}`;
  }
}

const fetchConfig = new Configuration({
  basePath: getBaseApiURL(),
  headers: {
    Authorization: constructAccessToken(process.env.NEXT_PUBLIC_API_TOKEN ?? ''), // 123 for local
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Auth-Token': 'bypass',
    credentials: 'include',
  },
  queryParamsStringify(params) {
    return querystring(params);
  },
  middleware: [acceptLanguageMiddleware],
});

export const donationsApiForClient = new DonationsApi(fetchConfig);
export const meApiForClient = new MeApi(fetchConfig);
export const projectsApiForClient = new ProjectsApi(fetchConfig);
export const projectFaqsApiForClient = new ProjectFaqsApi(fetchConfig);
export const projectNewsApiForClient = new ProjectNewsApi(fetchConfig);
export const searchApiForClient = new SearchApi(fetchConfig);
export const taxonomyApiForClient = new TaxonomyApi(fetchConfig);
export const subprojectsApiForClient = new SubprojectsApi(fetchConfig);
export const projectRewardsApiForClient = new ProjectRewardsApi(fetchConfig);
